import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import HeaderNav from "@/components/HeaderNav";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { ArrowLeft, Sparkles, Lock, Clock, DollarSign, Wrench, Bookmark, BookmarkCheck } from "lucide-react";

function renderMarkdown(md) {
  // extremely small markdown → HTML (headings, bold, lists, paragraphs)
  const lines = md.split("\n");
  const html = [];
  let inList = null;
  const flushList = () => { if (inList) { html.push(`</${inList}>`); inList = null; } };
  for (const line of lines) {
    if (/^## /.test(line)) { flushList(); html.push(`<h2>${line.slice(3)}</h2>`); continue; }
    if (/^### /.test(line)) { flushList(); html.push(`<h3>${line.slice(4)}</h3>`); continue; }
    if (/^- /.test(line)) {
      if (inList !== "ul") { flushList(); html.push("<ul>"); inList = "ul"; }
      html.push(`<li>${inline(line.slice(2))}</li>`); continue;
    }
    if (/^\d+\. /.test(line)) {
      if (inList !== "ol") { flushList(); html.push("<ol>"); inList = "ol"; }
      html.push(`<li>${inline(line.replace(/^\d+\. /, ""))}</li>`); continue;
    }
    if (!line.trim()) { flushList(); continue; }
    flushList(); html.push(`<p>${inline(line)}</p>`);
  }
  flushList();
  return html.join("");
  function inline(s) {
    return s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
            .replace(/`([^`]+)`/g, "<code>$1</code>");
  }
}

export default function HustleDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [h, setH] = useState(null);
  const [blueprint, setBlueprint] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { api.get(`/hustles/${id}`).then(r => setH(r.data)); }, [id]);
  useEffect(() => { setSaved((user?.saved || []).includes(id)); }, [user, id]);

  const toggleSave = async () => {
    if (!user) return toast.error("Sign in to save");
    try {
      const { data } = await api.post(`/hustles/${id}/save`);
      setSaved(data.saved);
      refresh();
      toast.success(data.saved ? "Added to shortlist" : "Removed from shortlist");
    } catch { toast.error("Failed"); }
  };

  const genBlueprint = async () => {
    if (!user) return nav("/");
    if (!user.pro) return nav("/pricing");
    setLoading(true); setBlueprint("");
    try {
      const res = await fetch(`${API}/hustles/${id}/blueprint`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("hc_token")}` },
      });
      if (!res.ok) {
        if (res.status === 402) return nav("/pricing");
        throw new Error("Failed");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setBlueprint(acc);
      }
    } catch (e) {
      toast.error("Failed to generate blueprint");
    } finally { setLoading(false); }
  };

  if (!h) return <><HeaderNav/><div className="max-w-4xl mx-auto p-12 text-center text-[var(--stone)]">Loading…</div></>;

  return (
    <div className="min-h-screen">
      <HeaderNav/>
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10">
        <Link to="/app" className="inline-flex items-center gap-2 text-sm text-[var(--stone)] hover:text-[var(--forest)] mb-6" data-testid="back-link">
          <ArrowLeft size={16}/> Back
        </Link>

        <div className="tag-pill mb-3">{h.category}</div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <h1 className="font-display text-4xl sm:text-6xl font-black leading-tight" data-testid="hustle-title">{h.name}</h1>
          <button onClick={toggleSave} data-testid="detail-save-btn"
            className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 font-semibold text-sm transition
              ${saved ? "border-[var(--copper)] bg-[var(--copper)] text-white"
                      : "border-[var(--charcoal)] bg-transparent text-[var(--charcoal)] hover:bg-[var(--charcoal)] hover:text-[var(--alabaster)]"}`}>
            {saved ? <><BookmarkCheck size={16}/> Saved</> : <><Bookmark size={16}/> Save</>}
          </button>
        </div>
        <p className="text-xl text-[var(--stone)] mt-3 italic">"{h.tagline}"</p>
        <p className="mt-5 text-lg leading-relaxed">{h.blurb}</p>

        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <div className="card-warm p-5">
            <DollarSign className="text-[var(--copper)] mb-2" size={22}/>
            <div className="caption">Monthly earnings</div>
            <div className="font-display text-2xl font-bold mt-1 font-mono">${h.monthly_low.toLocaleString()}–${h.monthly_high.toLocaleString()}</div>
          </div>
          <div className="card-warm p-5">
            <Clock className="text-[var(--copper)] mb-2" size={22}/>
            <div className="caption">Time / week</div>
            <div className="font-display text-2xl font-bold mt-1 font-mono">{h.hours_low}–{h.hours_high}h</div>
          </div>
          <div className="card-warm p-5">
            <Wrench className="text-[var(--copper)] mb-2" size={22}/>
            <div className="caption">Startup cost</div>
            <div className="font-display text-2xl font-bold mt-1 font-mono">${h.startup_cost}</div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <span className="caption">Tools:</span>
          {(h.tools || []).map(t => <span key={t} className="tag-pill">{t}</span>)}
        </div>

        <div className="mt-10 card-warm p-6 sm:p-8" style={{background: user?.pro ? "#fff" : "var(--alabaster-2)"}}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="caption flex items-center gap-2"><Sparkles size={12}/> AI Blueprint</div>
              <h2 className="font-display text-2xl sm:text-3xl font-black mt-1">Your personalized start guide</h2>
              <p className="text-[var(--stone)] mt-1">Written by Claude, tailored to your quiz answers.</p>
            </div>
            {user?.pro ? (
              <button onClick={genBlueprint} disabled={loading} className="btn-copper" data-testid="generate-blueprint-btn">
                <Sparkles size={16}/> {loading ? "Generating…" : (blueprint ? "Regenerate" : "Generate blueprint")}
              </button>
            ) : (
              <Link to="/pricing" className="btn-primary" data-testid="unlock-blueprint-btn"><Lock size={16}/> Unlock with Pro</Link>
            )}
          </div>
          {blueprint && (
            <article className="prose-blueprint mt-6 pt-6 border-t border-[var(--border-warm)]"
                     dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(blueprint), {
                       ALLOWED_TAGS: ["h2","h3","p","ul","ol","li","strong","em","code","br"],
                       ALLOWED_ATTR: [],
                     }) }} data-testid="blueprint-content"/>
          )}
        </div>
      </div>
    </div>
  );
}
